import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Upload, Loader2, Trash2, ArrowLeft, CreditCard, Check, Link2, Unlink, EyeOff, FileSpreadsheet, BellRing, AlertTriangle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/expenses/visa`;
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

export const statusTone = (st) => {
  if (st === 'matched' || st === 'lost_receipt') return { row: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-600 text-white', label: st === 'matched' ? 'Expensed' : 'Lost receipt' };
  if (st === 'ignored') return { row: 'bg-slate-100 border-slate-300 opacity-70', badge: 'bg-slate-200 text-slate-900', label: 'Not an expense' };
  return { row: 'bg-amber-50 border-amber-300', badge: 'bg-amber-500 text-black', label: 'Needs expense' };
};

const errMsg = (err, fallback) => {
  const d = err?.response?.data;
  if (d && typeof d === 'object' && d.detail) return typeof d.detail === 'string' ? d.detail : fallback;
  if (err?.response?.status) return `${fallback} (server replied ${err.response.status})`;
  return fallback;
};

// ── Admin: list + upload ──────────────────────────────────────────────────────
export default function VisaStatements() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/statements`); setList(r.data.statements || []); } catch (e) { toast.error(errMsg(e, 'Failed to load statements')); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const anyProcessing = list.some((s) => s.status === 'processing');
  useEffect(() => {
    if (!anyProcessing || selected) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [anyProcessing, selected, load]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await axios.post(`${API}/statements`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      toast.success('Statement received — reading it now. This takes a couple of minutes; you can leave this page.');
      await load(); setSelected(r.data.id);
    } catch (err) { toast.error(errMsg(err, 'Could not upload the statement')); }
    setUploading(false);
  };

  if (selected) return <StatementDetail id={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="space-y-4" data-testid="visa-statements">
      <label className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-xl p-6 cursor-pointer text-slate-700 hover:text-slate-900 hover:border-slate-400 border-slate-300 ${uploading ? 'opacity-60 pointer-events-none' : ''}`} data-testid="visa-upload-label">
        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
        <span className="text-sm">{uploading ? 'Uploading…' : 'Upload the Visa statement PDF'}</span>
        <input type="file" accept="application/pdf" className="hidden" onChange={upload} disabled={uploading} data-testid="visa-upload-input" />
      </label>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500 text-center" data-testid="visa-empty">No statements yet.</p>
      ) : list.map((s) => (
        <button key={s.id} onClick={() => setSelected(s.id)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-500" data-testid={`visa-stmt-${s.id}`} data-status={s.status}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-slate-900 font-semibold flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-700" />
                {s.status === 'ready' ? <>{s.bank || 'Visa'} statement {fmtD(s.period_start)} – {fmtD(s.period_end)}</> : s.filename}
              </p>
              <p className="text-xs text-slate-500">{s.filename} · {s.cardholders?.length || 0} cards · uploaded by {s.uploaded_by_name} {new Date(s.uploaded_at).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {s.status === 'processing' && <span className="px-2 py-1 rounded bg-blue-600 text-white inline-flex items-center gap-1" data-testid={`visa-stmt-processing-${s.id}`}><Loader2 className="w-3 h-3 animate-spin" />Reading…</span>}
              {s.status === 'failed' && <span className="px-2 py-1 rounded bg-red-600 text-white" data-testid={`visa-stmt-failed-${s.id}`}>Failed — open to retry</span>}
              {s.status === 'ready' && (
                <>
                  <span className="px-2 py-1 rounded bg-emerald-600 text-white">{s.summary.matched + s.summary.lost_receipt} expensed</span>
                  <span className="px-2 py-1 rounded bg-amber-500 text-black font-semibold">{s.summary.unmatched} need expense</span>
                  <span className="text-slate-700">{fmt(s.summary.total)}</span>
                </>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ── Admin: one statement, grouped by cardholder ───────────────────────────────
function StatementDetail({ id, onBack }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/statements/${id}`); setSt(r.data); } catch (e) { toast.error(errMsg(e, 'Failed to load')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const processing = st?.status === 'processing';
  useEffect(() => {
    if (!processing) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [processing, load]);

  const act = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(errMsg(e, 'Failed')); } setBusy(false); };
  const remap = (last4, employee_id) => act(() => axios.put(`${API}/statements/${id}/cardholders/${last4}`, { employee_id: employee_id || null }), 'Card owner updated');
  const del = () => { if (window.confirm('Delete this statement? Expense lines are not affected.')) act(async () => { await axios.delete(`${API}/statements/${id}`); onBack(); }); };
  const nudge = () => {
    const names = [...new Set(st.transactions.filter((t) => t.amount > 0 && (!t.match?.status || t.match.status === 'unmatched') && t.employee_id).map((t) => t.cardholder))];
    if (!names.length) return toast.info('Nobody has amber charges on this statement');
    if (!window.confirm(`Email + push ${names.length} cardholder${names.length > 1 ? 's' : ''} (${names.join(', ')}) their charges that still need an expense?`)) return;
    act(async () => {
      const r = await axios.post(`${API}/statements/${id}/nudge`);
      const p = r.data.nudge.people;
      toast.success(`Nudged ${p.length} cardholder${p.length !== 1 ? 's' : ''} — ${p.filter((x) => x.emailed).length} emailed, ${p.filter((x) => x.pushed).length} pushed`);
    });
  };
  const token = localStorage.getItem('token');

  if (!st) return <div className="text-slate-500 text-sm p-6">Loading…</div>;
  if (st.status === 'processing' || st.status === 'failed') {
    const failed = st.status === 'failed';
    return (
      <div className="space-y-4" data-testid="visa-statement-detail">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm" data-testid="visa-back"><ArrowLeft className="w-4 h-4" />All statements</button>
        <div className={`rounded-xl border p-6 text-center space-y-3 ${failed ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`} data-testid={failed ? 'visa-processing-failed' : 'visa-processing'}>
          {failed ? <AlertTriangle className="w-8 h-8 text-red-700 mx-auto" /> : <Loader2 className="w-8 h-8 text-blue-700 mx-auto animate-spin" />}
          <p className="text-slate-900 font-semibold">{failed ? 'Could not read this statement' : 'Reading the statement…'}</p>
          <p className="text-sm text-slate-700">{failed ? st.error : `${st.filename} — turning the pages into images and pulling out every cardholder's charges. This usually takes 1–3 minutes; you can leave this page and come back.`}</p>
          <p className="text-xs text-slate-500">{st.filename} · uploaded by {st.uploaded_by_name} {new Date(st.uploaded_at).toLocaleString()}</p>
          <div className="flex items-center justify-center gap-2">
            {failed && <Button size="sm" onClick={() => act(() => axios.post(`${API}/statements/${id}/retry`), 'Reading it again…')} disabled={busy} data-testid="visa-retry"><RefreshCw className="w-3 h-3 mr-1" />Retry</Button>}
            <Button variant="outline" size="sm" onClick={del} disabled={busy} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="visa-delete"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
          </div>
        </div>
      </div>
    );
  }
  const byCard = st.cardholders.map((ch) => ({ ...ch, tx: st.transactions.filter((t) => t.last4 === ch.last4) }));
  const lastNudge = st.nudges?.length ? st.nudges[st.nudges.length - 1] : null;

  return (
    <div className="space-y-4" data-testid="visa-statement-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm" data-testid="visa-back"><ArrowLeft className="w-4 h-4" />All statements</button>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 text-emerald-700"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-400 inline-block" />expensed</span>
          <span className="inline-flex items-center gap-1 text-amber-700"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-400 inline-block" />needs an expense</span>
          <a href={`${API}/statements/${id}/export.xlsx?token=${token}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-emerald-200 text-emerald-800 hover:bg-emerald-50" data-testid="visa-export-xlsx"><FileSpreadsheet className="w-3 h-3" />Download Excel</a>
          <Button variant="outline" size="sm" onClick={nudge} disabled={busy || st.summary.unmatched === 0} className="text-amber-800 border-amber-200 hover:bg-amber-50" data-testid="visa-nudge"><BellRing className="w-3 h-3 mr-1" />Nudge cardholders</Button>
          <Button variant="outline" size="sm" onClick={del} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="visa-delete"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
        </div>
      </div>
      <div>
        <h3 className="text-slate-900 font-semibold">{st.bank || 'Visa'} statement {fmtD(st.period_start)} – {fmtD(st.period_end)}</h3>
        <p className="text-xs text-slate-500">{st.summary.charges} charges · {fmt(st.summary.total)} · {st.summary.matched + st.summary.lost_receipt} expensed · <span className="text-amber-700 font-semibold">{st.summary.unmatched} need an expense</span></p>
        <p className="text-[11px] text-slate-500" data-testid="visa-nudge-status">
          {lastNudge
            ? `Last nudged ${new Date(lastNudge.at).toLocaleDateString()} by ${lastNudge.by} — ${lastNudge.people.length} cardholder${lastNudge.people.length !== 1 ? 's' : ''}${lastNudge.people.length ? ` (${lastNudge.people.map((p) => p.name).join(', ')})` : ''}`
            : `Cardholders with amber charges are nudged automatically one week after upload (${new Date(new Date(st.uploaded_at).getTime() + 7 * 86400000).toLocaleDateString()}).`}
        </p>
      </div>
      {byCard.map((ch) => (
        <div key={ch.last4} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`visa-card-${ch.last4}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-white">
            <p className="text-slate-900 font-semibold flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-700" />{ch.name} <span className="font-mono text-xs text-slate-500">…{ch.last4}</span></p>
            <label className="text-xs text-slate-700 flex items-center gap-2">Employee
              <select value={ch.employee_id || ''} onChange={(e) => remap(ch.last4, e.target.value)} disabled={busy} className="bg-white border border-slate-300 rounded px-2 py-1 text-slate-900" data-testid={`visa-card-owner-${ch.last4}`}>
                <option value="">— not linked —</option>
                {st.employees.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </label>
          </div>
          <div className="divide-y divide-slate-700/50">
            {ch.tx.length === 0 && <p className="text-xs text-slate-500 px-4 py-3">No charges this period.</p>}
            {ch.tx.map((t) => <TxRow key={t.id} t={t} st={st} busy={busy} act={act} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function TxRow({ t, st, busy, act }) {
  const [pick, setPick] = useState('');
  const m = t.match || {};
  const credit = (t.amount || 0) <= 0;
  const tone = credit ? { row: 'bg-white border-slate-200', badge: 'bg-slate-200 text-slate-900', label: 'Credit / payment' } : statusTone(m.status);
  const near = (v) => v != null && Math.abs((Number(v) || 0) - t.amount) < 5;
  const cands = (st.candidate_lines?.[t.employee_id] || []).filter((l) => near(l.invoice_total) || (l.card_slip_line_id && near(l.card_total)));
  const candLabel = (l) => `${fmtD(l.date)} ${l.supplier} ${fmt(l.invoice_total)}${l.card_slip_line_id && near(l.card_total) ? ` + tip = ${fmt(l.card_total)}` : ''}${l.tip_for_line_id ? ' (tip)' : ''} (${l.report_status})`;
  const base = `${API}/statements/${st.id}/tx/${t.id}`;
  return (
    <div className={`px-4 py-2 border-l-4 ${tone.row} flex flex-wrap items-center gap-3`} data-testid={`visa-tx-${t.id}`} data-status={credit ? 'credit' : (m.status || 'unmatched')}>
      <span className="text-xs text-slate-700 w-14">{fmtD(t.trans_date)}</span>
      <span className="flex-1 min-w-[160px] text-sm text-slate-900 truncate" title={t.description}>{t.description}</span>
      <span className={`text-sm font-mono ${credit ? 'text-slate-500' : 'text-slate-900'} w-24 text-right`}>{fmt(t.amount)}</span>
      <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${tone.badge}`}>{tone.label}</span>
      {!credit && (m.status === 'matched' || m.status === 'lost_receipt') && (
        <span className="text-xs text-emerald-800 truncate max-w-[360px]" title={`${m.report_title}${m.note ? ` · ${m.note}` : ''}`}>{m.report_title} · {m.report_status}{m.auto ? ' · auto' : ''}{m.note ? <span className="text-emerald-700" data-testid={`visa-tip-note-${t.id}`}> · {m.note}</span> : null}</span>
      )}
      {!credit && (m.status === 'matched' || m.status === 'ignored') && (
        <button disabled={busy} onClick={() => act(() => axios.post(`${base}/unmatch`), 'Unmatched')} className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`visa-unmatch-${t.id}`}><Unlink className="w-3 h-3" />undo</button>
      )}
      {!credit && (!m.status || m.status === 'unmatched') && (
        <div className="flex items-center gap-1">
          {t.employee_id && (
            <>
              <select value={pick} onChange={(e) => setPick(e.target.value)} className="bg-white border border-slate-300 rounded px-1 py-0.5 text-xs text-slate-900 max-w-[200px]" data-testid={`visa-pick-${t.id}`}>
                <option value="">match to a line…</option>
                {cands.map((l) => <option key={l.line_id} value={`${l.report_id}|${l.line_id}`}>{candLabel(l)}</option>)}
              </select>
              <button disabled={busy || !pick} onClick={() => { const [report_id, line_id] = pick.split('|'); act(() => axios.post(`${base}/match`, { report_id, line_id }), 'Matched'); }} className="text-xs text-emerald-700 hover:text-slate-900 disabled:opacity-40 inline-flex items-center gap-1" data-testid={`visa-match-${t.id}`}><Link2 className="w-3 h-3" />match</button>
              <button disabled={busy} onClick={() => act(() => axios.post(`${base}/lost-receipt`, {}), 'Lost-receipt line added to their expense report')} className="text-xs text-amber-800 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`visa-lost-${t.id}`}><Check className="w-3 h-3" />lost receipt</button>
            </>
          )}
          <button disabled={busy} onClick={() => act(() => axios.post(`${base}/ignore`, {}), 'Marked as not an expense')} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`visa-ignore-${t.id}`}><EyeOff className="w-3 h-3" />not an expense</button>
        </div>
      )}
    </div>
  );
}
